import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import { Building2, ExternalLink, Link2, MoreHorizontal } from 'lucide-react';
import type { CrmActivity, CrmContact, CrmDealDetail, CrmDealListItem, CrmStage, CrmUserOption } from '@ip/shared-types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCrmApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { initialsFromName } from '@/lib/hr';
import { ActivityComposer, type ComposerFocusTarget } from '../ActivityComposer';
import { ActivityTimeline, type TimelineFilter } from '../ActivityTimeline';
import { findCard, isTypingTarget, stageShortcutTarget } from './boardKeyboard';
import { contactName } from './contact';
import { DealFacts } from './DealFacts';
import { InlineField } from './InlineField';
import { editorKeyHandlers, type FocusRequest } from './inlineEditing';
import { NextStepCard } from './NextStepCard';
import { StageStepper } from './StageStepper';
import { fmtDate, healthLabel, isClosedDeal, relativeAgoLabel, type StageIndex } from './dealMath';
import { nextStepFor, type NextStep, type NextStepAction } from './nextStep';
import { TONE_BORDER, TONE_TEXT } from './tones';
import type { MoveDealFn } from './useDealBoard';

/** Activities are loaded in one page — a deal with more history than this is not a real case. */
const ACTIVITY_PAGE = 200;

/** One parent-driven "open / focus this" channel: a facts row or the composer with a type. */
type FocusTarget = 'value' | 'date' | ComposerFocusTarget;

interface Props {
  nip: string;
  dealId: string | null;
  /**
   * The board's list item — rendered as the header immediately, and merged
   * over the loaded detail so an optimistic stage move (or its rollback) on
   * the board is reflected in the sheet without a refetch.
   */
  initial?: CrmDealListItem;
  stages: ReadonlyArray<CrmStage>;
  stageIndex: StageIndex;
  now: Date;
  pending: boolean;
  currentUserId: string | null;
  currentUserName: string;
  onClose: () => void;
  /** Close the sheet and go elsewhere (account page, lead queue). */
  onNavigate: (to: string) => void;
  onDealChanged: (detail: CrmDealDetail) => void;
  onActivityLogged: (dealId: string, occurredAt: string) => void;
  onMove: MoveDealFn;
}

function HealthBadge({ deal, now }: { deal: CrmDealListItem; now: Date }) {
  const health = healthLabel(deal, now);
  if (!health) return null;
  return (
    <Badge variant="outline" className={cn(TONE_BORDER[health.tone], TONE_TEXT[health.tone])}>
      {health.label}
    </Badge>
  );
}

/**
 * The deal "modal": a right-hand sheet (the board stays visible) with the
 * stage stepper, the activity history and the editable facts. It never
 * reloads the board — every change flows back through the callbacks.
 *
 * The dossier itself is keyed by deal id: opening B while A is up remounts
 * it (A's facts/draft can never land on B), and closing keeps rendering the
 * last id for the 300 ms slide-out.
 */
export function DealDetailSheet({
  nip,
  dealId,
  initial,
  stages,
  stageIndex,
  now,
  pending,
  currentUserId,
  currentUserName,
  onClose,
  onNavigate,
  onDealChanged,
  onActivityLogged,
  onMove,
}: Props) {
  const { listCrmUsers } = useCrmApi();

  // What the sheet last opened for — `dealId` (and the board item derived
  // from it) is already null during the exit animation and by the time Radix
  // fires `onCloseAutoFocus`.
  const lastOpenRef = useRef<{ id: string; initial?: CrmDealListItem } | null>(null);
  if (dealId) lastOpenRef.current = { id: dealId, initial };
  const shownId = dealId ?? lastOpenRef.current?.id ?? null;
  const shownInitial = dealId ? initial : lastOpenRef.current?.initial;

  // Users are only needed by the owner picker — fetched on first edit, once
  // per sheet lifetime (per company).
  const usersCache = useRef<{ nip: string; promise: Promise<CrmUserOption[]> } | null>(null);
  const loadUsers = useCallback(() => {
    if (usersCache.current?.nip !== nip) {
      const promise = listCrmUsers(nip).catch((error: unknown) => {
        usersCache.current = null;
        throw error;
      });
      usersCache.current = { nip, promise };
    }
    return usersCache.current.promise;
  }, [nip, listCrmUsers]);

  // An unsaved composer entry costs one extra Esc: the first blurs the box
  // and warns, the second closes. Typing again re-arms the guard.
  const [composerDirty, setComposerDirty] = useState(false);
  const escArmed = useRef(false);
  useEffect(() => {
    escArmed.current = false;
  }, [composerDirty]);

  // `]` / `[` / `W` / `L` inside the sheet — never while typing, never when a
  // nested dialog (resume confirmation) owns the keyboard. A nested Radix
  // layer is portaled outside the sheet DOM, hence the `contains` check.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!shownInitial || pending || isClosedDeal(shownInitial, stageIndex.byId.get(shownInitial.stageId))) return;
    if (isTypingTarget(event)) return;
    if (!(event.currentTarget as HTMLElement).contains(event.target as Node)) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = stageShortcutTarget(event.key, shownInitial, stageIndex);
    if (!target) return;
    event.preventDefault();
    void onMove(shownInitial.id, target.stageId);
  };

  return (
    <Sheet open={!!dealId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl"
        onKeyDown={handleKeyDown}
        onEscapeKeyDown={(event) => {
          if (composerDirty && !escArmed.current) {
            event.preventDefault();
            escArmed.current = true;
            (document.activeElement as HTMLElement | null)?.blur?.();
            toast(t('crm.deals.detail.unsavedNote'));
          }
        }}
        onCloseAutoFocus={(event) => {
          // Opened by state, not a Radix trigger — put focus back on the card.
          // If the card is gone (deleted, filtered out) let Radix restore
          // focus wherever it was.
          const card = lastOpenRef.current ? findCard(lastOpenRef.current.id) : null;
          if (!card) return;
          event.preventDefault();
          card.focus();
        }}
      >
        {shownId ? (
          <DealDossier
            key={shownId}
            nip={nip}
            dealId={shownId}
            initial={shownInitial}
            stages={stages}
            stageIndex={stageIndex}
            now={now}
            pending={pending}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            onClose={onClose}
            onNavigate={onNavigate}
            onDealChanged={onDealChanged}
            onActivityLogged={onActivityLogged}
            onMove={onMove}
            onComposerDirtyChange={setComposerDirty}
            loadUsers={loadUsers}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

interface DossierProps extends Omit<Props, 'dealId'> {
  dealId: string;
  onComposerDirtyChange: (dirty: boolean) => void;
  loadUsers: () => Promise<CrmUserOption[]>;
}

/** Everything inside the sheet for ONE deal — mounted fresh per id. */
function DealDossier({
  nip,
  dealId,
  initial,
  stages,
  stageIndex,
  now,
  pending,
  currentUserId,
  currentUserName,
  onClose,
  onNavigate,
  onDealChanged,
  onActivityLogged,
  onMove,
  onComposerDirtyChange,
  loadUsers,
}: DossierProps) {
  const { getDeal, listActivities, listContacts, updateDeal } = useCrmApi();
  const isMobile = useIsMobile();

  const [detail, setDetail] = useState<CrmDealDetail | null>(null);
  const [activities, setActivities] = useState<CrmActivity[]>([]);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('all');
  const [focusRequest, setFocusRequest] = useState<FocusRequest<FocusTarget> | null>(null);
  // Read once: the board item's account id lets contacts load in parallel
  // with the detail; a deep link without it waits for the detail's.
  const [accountHint] = useState(() => initial?.accountId ?? null);
  // `onClose` changes identity with the URL params; going through a ref keeps
  // the load effect from re-running on it.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const loadSeq = useRef(0);

  useEffect(() => {
    const seq = ++loadSeq.current;
    setLoading(true);
    (async () => {
      try {
        const [dealDetail, activityList, hintedContacts] = await Promise.all([
          getDeal(nip, dealId),
          listActivities(nip, { dealId, limit: ACTIVITY_PAGE }),
          accountHint ? listContacts(nip, accountHint) : null,
        ]);
        const contactList = hintedContacts ?? (await listContacts(nip, dealDetail.accountId));
        if (seq !== loadSeq.current) return;
        setDetail(dealDetail);
        setActivities(activityList);
        setContacts(contactList);
        // Upsert into the board too: a deep-linked deal outside the 30-day
        // closed window is not on the board yet, and `moveDeal` (resume,
        // stepper) silently ignores ids it does not know.
        onDealChanged(dealDetail);
      } catch {
        if (seq !== loadSeq.current) return;
        // A deleted or forbidden deal must not leave an endless skeleton open
        // (closing also clears the `?deal=` param) — LeadDetailSheet convention.
        toast.error(t('crm.deals.detail.notFound'));
        onCloseRef.current();
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    })();
  }, [nip, dealId, accountHint, getDeal, listActivities, listContacts, onDealChanged]);

  const refetchActivities = useCallback(async () => {
    try {
      setActivities(await listActivities(nip, { dealId, limit: ACTIVITY_PAGE }));
    } catch {
      // The optimistic row stays; the next open reloads everything.
    }
  }, [nip, dealId, listActivities]);

  // A stage change (stepper, shortcut, undo, the board itself) writes a
  // `stage_change` row server-side — pull it in when the board's item moves.
  // `stageEnteredAt` is part of the key on purpose: the optimistic patch and
  // the server's answer differ by milliseconds, so the second refetch (the
  // one that actually sees the new row) always fires.
  const lastStageKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    const key = initial ? `${initial.stageId}:${initial.closedAt ?? ''}:${initial.stageEnteredAt}` : undefined;
    if (lastStageKey.current !== undefined && key !== undefined && key !== lastStageKey.current) {
      void refetchActivities();
    }
    lastStageKey.current = key;
  }, [initial, refetchActivities]);

  // The board item wins over the loaded detail (optimistic stage moves and
  // their rollbacks); after `applyDeal` it also carries the detail's own keys,
  // so a lostReason set by a confirmed close is visible too.
  const deal = useMemo<CrmDealDetail | null>(() => (detail ? { ...detail, ...initial } : null), [detail, initial]);

  const header: CrmDealListItem | null = deal ?? initial ?? null;
  const stage = header ? stageIndex.byId.get(header.stageId) : undefined;
  const closed = header ? isClosedDeal(header, stage) : false;
  const primaryContact = deal?.primaryContactId
    ? contacts.find((contact) => contact.id === deal.primaryContactId)
    : undefined;
  const contactDisplayName = primaryContact ? contactName(primaryContact) : null;

  const nextStep = useMemo<NextStep | null>(
    () =>
      deal
        ? nextStepFor({
            deal,
            activities,
            stageIndex,
            now,
            contactName: contactDisplayName,
            contactHasPhone: !!primaryContact?.phone,
          })
        : null,
    [deal, activities, stageIndex, now, contactDisplayName, primaryContact?.phone],
  );

  const handleDetail = useCallback(
    (next: CrmDealDetail) => {
      // The board still learns about the save; only this dossier ignores a
      // response for a deal it does not show.
      if (next.id === dealId) setDetail(next);
      onDealChanged(next);
    },
    [dealId, onDealChanged],
  );

  const focus = useCallback((target: FocusTarget) => setFocusRequest({ target, nonce: Date.now() }), []);

  const handleNextStepAction = (action: NextStepAction, step: NextStep) => {
    if (!deal) return;
    switch (action) {
      case 'moveDate':
      case 'setDate':
        focus('date');
        break;
      case 'enterValue':
        focus('value');
        break;
      case 'call':
      case 'logCall':
        focus('composer:call');
        break;
      case 'won':
        if (stageIndex.won) void onMove(deal.id, stageIndex.won.id);
        break;
      case 'lost':
        if (stageIndex.lost) void onMove(deal.id, stageIndex.lost.id);
        break;
      case 'moveNext':
        if (step.nextStageId) void onMove(deal.id, step.nextStageId);
        break;
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/${nip}/crm/szanse?deal=${dealId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('crm.deals.detail.linkCopied'));
    } catch {
      toast.error(t('errors.saveFailed'));
    }
  };

  if (!header) {
    return (
      <div className="space-y-3 p-6">
        <SheetTitle className="sr-only">{t('crm.deals.detail.overline')}</SheetTitle>
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </div>
    );
  }

  // The composer is a non-scrolling sibling above the list, so the list's
  // day labels stick at its own top edge.
  const historyPane = (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b px-6 pt-4 pb-3">
        <ActivityComposer
          nip={nip}
          dealId={header.id}
          accountId={header.accountId}
          contactId={deal?.primaryContactId ?? null}
          contactPhone={primaryContact?.phone ?? null}
          authorName={currentUserName}
          focusRequest={focusRequest}
          onDirtyChange={onComposerDirtyChange}
          onOptimistic={(activity) => setActivities((prev) => [activity, ...prev])}
          onSaved={async (occurredAt) => {
            onActivityLogged(header.id, occurredAt);
            await refetchActivities();
          }}
          onFailed={(tempId) => setActivities((prev) => prev.filter((activity) => activity.id !== tempId))}
        />
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
        {nextStep && !closed ? (
          <NextStepCard
            step={nextStep}
            contactPhone={primaryContact?.phone ?? null}
            disabled={pending}
            onAction={handleNextStepAction}
          />
        ) : null}
        {loading && !deal ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <ActivityTimeline
            activities={activities}
            stages={stages}
            createdAt={deal?.createdAt ?? null}
            filter={timelineFilter}
            onFilterChange={setTimelineFilter}
            footerLink={{
              to: `/${nip}/crm/klienci/${header.accountId}`,
              label: t('crm.deals.timeline.fullAccountHistory'),
            }}
          />
        )}
      </div>
    </div>
  );

  const factsPane = deal ? (
    <div className="px-6 py-4">
      <DealFacts
        nip={nip}
        deal={deal}
        stage={stage}
        contacts={contacts}
        loadUsers={loadUsers}
        currentUserId={currentUserId}
        now={now}
        loading={loading && !detail}
        onSaved={handleDetail}
        onCallClick={() => focus('composer:call')}
        focusRequest={focusRequest}
      />
    </div>
  ) : (
    <div className="space-y-3 px-6 py-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton key={index} className="h-4 w-full" />
      ))}
    </div>
  );

  return (
    <>
      <SheetHeader className="space-y-3 border-b px-6 pt-5 pb-4">
        <div className="flex items-center justify-between gap-3 pr-8">
          <span className="text-muted-foreground text-[11px] tracking-wide uppercase">
            {t('crm.deals.detail.overline')} ·{' '}
            {closed ? (stage?.isWon ? t('crm.deals.stepper.won') : t('crm.deals.stepper.lost')) : header.stageName}
          </span>
          <span className="flex items-center gap-2">
            <HealthBadge deal={header} now={now} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t('common.actions')}>
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onNavigate(`/${nip}/crm/klienci/${header.accountId}`)}>
                  <ExternalLink className="size-4" />
                  {t('crm.deals.detail.openAccount')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void copyLink()}>
                  <Link2 className="size-4" />
                  {t('crm.deals.detail.copyLink')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        </div>
        <SheetTitle asChild>
          <div role="heading" aria-level={2} className="text-lg font-semibold">
            {deal ? (
              <InlineField<string>
                display={<span>{deal.name}</span>}
                displayClassName="text-lg font-semibold"
                editor={(api) => (
                  <NameEditor initial={deal.name} saving={api.saving} onCommit={api.commit} onCancel={api.cancel} />
                )}
                onSave={async (name) => handleDetail(await updateDeal(nip, deal.id, { name }))}
              />
            ) : (
              header.name
            )}
          </div>
        </SheetTitle>
        <SheetDescription asChild>
          <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <button
              type="button"
              className="hover:text-foreground inline-flex items-center gap-1 hover:underline"
              onClick={() => onNavigate(`/${nip}/crm/klienci/${header.accountId}`)}
            >
              <Building2 className="size-3.5" />
              {header.accountName}
            </button>
            <span className="inline-flex items-center gap-1.5">
              {header.ownerDisplayName ? (
                <>
                  <Avatar className="size-5">
                    <AvatarFallback className="text-[9px]">{initialsFromName(header.ownerDisplayName)}</AvatarFallback>
                  </Avatar>
                  {header.ownerDisplayName}
                </>
              ) : (
                <span className="italic">{t('crm.deals.card.noOwner')}</span>
              )}
            </span>
            {deal ? (
              <span>
                {t('crm.deals.detail.createdAgo', {
                  date: fmtDate(deal.createdAt),
                  ago: relativeAgoLabel(deal.createdAt, now),
                })}
              </span>
            ) : null}
            {deal?.sourceLeadId ? (
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => onNavigate(`/${nip}/crm?lead=${deal.sourceLeadId}`)}
              >
                {t('crm.deals.detail.fromLead')}
              </button>
            ) : null}
          </div>
        </SheetDescription>
      </SheetHeader>

      <div className="bg-muted/30 border-b px-6 py-3">
        {deal ? (
          <StageStepper
            deal={deal}
            stageIndex={stageIndex}
            activities={activities}
            now={now}
            pending={pending}
            onMove={onMove}
          />
        ) : (
          <Skeleton className="h-7 w-full" />
        )}
      </div>

      {isMobile ? (
        <Tabs defaultValue="history" className="min-h-0 flex-1 gap-0">
          <TabsList className="mx-6 mt-3 w-[calc(100%-3rem)]">
            <TabsTrigger value="history" className="flex-1">
              {t('crm.deals.detail.tabs.history')}
            </TabsTrigger>
            <TabsTrigger value="data" className="flex-1">
              {t('crm.deals.detail.tabs.data')}
            </TabsTrigger>
          </TabsList>
          {/* Both panes stay mounted (hidden, not unmounted) so a half-typed composer entry survives a peek at "Dane". */}
          <TabsContent forceMount value="history" className="flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
            {historyPane}
          </TabsContent>
          <TabsContent forceMount value="data" className="min-h-0 flex-1 overflow-y-auto data-[state=inactive]:hidden">
            {factsPane}
          </TabsContent>
        </Tabs>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[3fr_2fr] divide-x">
          <div className="flex min-h-0 flex-col">{historyPane}</div>
          <div className="min-h-0 overflow-y-auto">{factsPane}</div>
        </div>
      )}
    </>
  );
}

/** Deal name editor: Enter saves, an empty name is refused inline, Esc reverts. */
function NameEditor({
  initial,
  saving,
  onCommit,
  onCancel,
}: {
  initial: string;
  saving: boolean;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  const [error, setError] = useState(false);
  const commit = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError(true);
      return;
    }
    if (trimmed === initial) {
      onCancel();
      return;
    }
    onCommit(trimmed);
  };
  return (
    <div className="w-full space-y-1">
      <Input
        autoFocus
        value={text}
        disabled={saving}
        aria-invalid={error || undefined}
        onChange={(event) => {
          setText(event.target.value);
          if (error) setError(false);
        }}
        onBlur={() => !saving && (text.trim() ? commit() : onCancel())}
        {...editorKeyHandlers(commit, onCancel)}
        className="h-9 text-lg font-semibold"
      />
      {error ? <p className="text-destructive text-xs font-normal">{t('crm.deals.detail.nameRequired')}</p> : null}
    </div>
  );
}
